import { CardProps, Flex } from "antd";
import VideoPreview from "./VideoPreview";
import { FontPreviewCard } from "./FontPreview";
import { PicturePreview } from "./PicturePreview";
import { useEffect } from "react";
import { BrandKitAsset } from "../../types";
import { AssetPurpose } from "./BrandKitAssets";
import { useCustomInvalidate } from "hooks/useCustomInvalidate";
import { DeleteAssetControl } from "./controls/DeleteAssetControl";
import { MusicPreview } from "./MusicPreview";
import { StagedBrandKitAsset } from "types/";
import { useSession } from "hooks/useSession/";

interface BrandKitAssetPreviewProps<T> extends CardProps {
  asset: T;
  setStagedAssets?: React.Dispatch<React.SetStateAction<StagedBrandKitAsset[]>>;
  stagedAssets?: StagedBrandKitAsset[];
  isNew?: boolean; // New assets' purpose can be edited
  purposeValue: AssetPurpose;
  onDeleteSuccess?: (assetId: string) => void;
  brandKitId: string;
}
export type FileOrPath =
  | { file: File; path?: never }
  | { file?: never; path: string };

export const BrandKitAssetPreview = <
  T extends {
    type: string;
    name: string;
    purpose?: AssetPurpose;
    uid?: string;
    asset_id?: string;
  } & FileOrPath,
>({
  asset,
  stagedAssets,
  setStagedAssets,
  isNew = false,
  purposeValue,
  onDeleteSuccess,
  brandKitId,
}: BrandKitAssetPreviewProps<T>) => {
  const type = asset.type;
  // Track the purpose changes in this variable derived from parent state
  const currentAsset = stagedAssets?.find((a) => a.uid === asset.uid);
  const { currentOrganization: organization } = useSession();
  const { customInvalidate } = useCustomInvalidate();

  // Set the purpose for music as it doesn't have a select
  useEffect(() => {
    setStagedAssets?.((prev) => {
      if (asset?.type === "Music") {
        return prev.map((a) =>
          a.uid === currentAsset?.uid ? { ...a, purpose: "Music" } : a
        );
      }
      return prev.map((a) =>
        a.uid === asset.uid ? { ...a, purpose: purposeValue } : a
      );
    });
  }, [setStagedAssets, asset.type, asset.uid, currentAsset?.uid, purposeValue]);

  const invalidateBrandkit = async () => {
    await customInvalidate(["brandkit", brandKitId]);
  };

  const deleteAction = (
    <DeleteAssetControl
      organizationId={organization?.id ?? ""}
      brandkitId={brandKitId}
      asset={asset as BrandKitAsset}
      onDeleteSuccess={onDeleteSuccess ?? (() => undefined)}
    />
  );

  const purposeItem = !isNew && (
    <Flex align="center" justify="space-between">
      <>{purposeValue}</>
      {deleteAction}
    </Flex>
  );
  return (
    <Flex vertical gap={10} style={{ width: "100%" }}>
      {type === "Picture" && (
        <PicturePreview
          fileName={asset.name}
          image={asset.file ?? asset.path}
          actions={purposeItem ? [purposeItem] : []}
          cardWidth={200}
        />
      )}
      {type === "Music" && (
        <MusicPreview
          asset={asset}
          showDeleteButton={!isNew}
          brandKitId={brandKitId}
          organizationId={organization?.id ?? ""}
          onDeleteSuccess={() => void invalidateBrandkit()}
        />
      )}
      {type === "Video" && (
        <VideoPreview
          fileName={asset.name}
          video={asset.file ?? asset.path}
          actions={purposeItem ? [purposeItem] : []}
          controls={true}
          hoverAutoplay={false}
          autoplay={true}
        />
      )}
      {type === "Font" && (
        <FontPreviewCard
          title={asset.name}
          font={asset.path ?? ""}
          actions={isNew ? [] : [deleteAction]}
          styles={{
            body: {
              padding: 10,
            },
            actions: {
              width: "fit-content",
              position: "absolute",
              right: 10,
              top: 0,
            },
          }}
        />
      )}
    </Flex>
  );
};
